import { useEffect } from 'react';
import { Control, FieldPath, FieldValues, useFormState, useWatch, useFormContext } from 'react-hook-form';
import { NumberInput } from '../primitive/number-input/NumberInput';
import './RFQRangeInput.scss';

interface RFQRangeInputFieldNames {
    single: string;
    min: string;
    max: string;
}

interface RFQRangeInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
    isRange: boolean;
    fieldNames: RFQRangeInputFieldNames;
    // eslint-disable-next-line react/no-unused-prop-types
    name: TName;
    control: Control<TFieldValues>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rules?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rulesMin?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rulesMax?: any;
    rangeClassName?: string;
}

export const RFQRangeInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    isRange,
    fieldNames,
    control,
    rules,
    rulesMin,
    rulesMax,
    rangeClassName = 'rfq-range',
}: RFQRangeInputProps<TFieldValues, TName>) => {
    const { trigger } = useFormContext<TFieldValues>();

    // Read touched/error state without registering the fields — using useController
    // would keep min/max registered even in SINGLE mode, blocking isValid.
    const { touchedFields, errors } = useFormState<TFieldValues>({ control });
    const typedTouched = touchedFields as Record<string, boolean>;
    const typedErrors = errors as Record<string, { type?: string; message?: string }>;

    const minValue = useWatch({ control, name: fieldNames.min as FieldPath<TFieldValues> });
    const maxValue = useWatch({ control, name: fieldNames.max as FieldPath<TFieldValues> });

    // Re-validate the sibling field when either value changes so cross-field
    // errors (min >= max) clear as soon as the user fixes the other input.
    useEffect(() => {
        if (isRange) {
            trigger(fieldNames.min as FieldPath<TFieldValues>);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxValue, isRange]);

    useEffect(() => {
        if (isRange) {
            trigger(fieldNames.max as FieldPath<TFieldValues>);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minValue, isRange]);

    // Show errors once the field has been touched, or if it already has a value
    // (e.g. pre-populated in edit mode — reset() doesn't mark fields as touched).
    // The cross-field validators (mustBeLessThan / mustBeGreaterThan) only fire when
    // the sibling field has a real value, so no extra gating is needed here.
    const minError = typedTouched[fieldNames.min] || minValue ? typedErrors[fieldNames.min]?.message : undefined;
    const maxError = typedTouched[fieldNames.max] || maxValue ? typedErrors[fieldNames.max]?.message : undefined;

    if (isRange) {
        return (
            <div className={rangeClassName}>
                <NumberInput
                    name={fieldNames.min as TName}
                    control={control}
                    rules={rulesMin}
                    error={minError}
                    controlledError
                    endAdornment="Min"
                />
                <NumberInput
                    name={fieldNames.max as TName}
                    control={control}
                    rules={rulesMax}
                    error={maxError}
                    controlledError
                    endAdornment="Max"
                />
            </div>
        );
    }

    return <NumberInput name={fieldNames.single as TName} control={control} rules={rules} />;
};
